import React, { MouseEvent } from 'react'
import { agnostifyUrl } from '../../../helpers/utils'
import { createGolNonContentDataAttributes } from '../../../helpers/tracking'
import { C008Props, PropositionType } from './types'
import { StyledContainer, StyledCopy, StyledIcon, StyledProposition } from './C008.styles'

/** C008 is a prop bar component, part of the main header and typically presents 3 propositional messages.
 * It has the ability to set up x3 content spots (icon and text) that link to different pages (spots will rotate on mobile / smaller view ports) - For use with the standard proposition bar
 * It can also have the ability to have a single banner for promoting events / emergency messaging (e.g. Black Friday, snow warnings, Covid-19 information) with x1 link and choose from a selection of background colors (that denote urgency and are brand aligned)
 */

const C008 = ({
  included = [
    {
      icon: '',
      copy: '',
      link: {
        url: '',
        title: ''
      }
    }
  ],
  backgroundColor,
  textColor,
  mobileLayout = 'slide',
  onLinkClick
}: C008Props): React.ReactElement => {
  if (included.length === 0) return null

  const isStandard = included.length > 1

  const addGolTracking = (evt: MouseEvent<HTMLElement>, ctaCopy: string) => {
    const dataAttributes = createGolNonContentDataAttributes(
      'link',
      'click',
      'cta link',
      `C008-${ctaCopy.toLowerCase()}`,
      'interaction'
    )
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event: MouseEvent<HTMLElement>, url: string, ctaCopy: string) => {
    if (onLinkClick) {
      onLinkClick(event, url)
    }
    addGolTracking(event, ctaCopy)
  }

  return (
    <div data-test='c008-container-wrapper'>
      <StyledContainer
        included={included}
        isStandard={isStandard}
        gap='none'
        testid='c008-container'
        colSize='fr' // makes the columns the same width regardless of content size
        stack='sm'
        backgroundColor={backgroundColor || 'white'}
        mobileLayout={mobileLayout}
      >
        {included.map((proposition: PropositionType, index: number) => {
          return (
            <StyledProposition
              key={`proposition-${index}`}
              forwardedAs={proposition.link?.url ? 'a' : undefined}
              href={proposition.link?.url ? agnostifyUrl(proposition.link.url) : undefined}
              onClick={(evt) =>
                !!proposition.link?.url && ctaClick(evt, proposition.link?.url, proposition.link?.title)
              }
              title={proposition.link?.title || undefined}
              data-test={`c008-proposition-${index}`}
              isStandard={isStandard}
              backgroundColor={backgroundColor || undefined}
              textColor={textColor || undefined}
              index={index}
              length={included.length}
              mobileLayout={mobileLayout}
            >
              {proposition.icon && (
                <StyledIcon
                  isStandard={isStandard}
                  src={proposition.icon}
                  data-test={`c008-icon-${index}`}
                  mobileLayout={mobileLayout}
                />
              )}
              <StyledCopy
                isStandard={isStandard}
                dangerouslySetInnerHTML={{ __html: proposition.copy }}
                data-test={`c008-copy-${index}`}
              />
            </StyledProposition>
          )
        })}
      </StyledContainer>
    </div>
  )
}

export default C008
